import { useState, useEffect, useCallback } from 'react';
import {
  Text,
  Button,
  Input,
  Switch,
  Field,
  Textarea,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  BuildingRegular,
  ImageRegular,
  SaveRegular,
  ArrowDownloadRegular,
  AddRegular,
} from '@fluentui/react-icons';
import { useIntl } from 'react-intl';
import {
  getCustomer,
  createCustomer,
  patchCustomer,
  importCustomer,
  type CustomerDocument,
  type CustomerPatch,
} from '../services/customer-service';
import { useApiToken } from '../hooks/useApiToken';

interface CustomerSettingsPageProps {
  customerId: string;
  onBack: () => void;
}

/**
 * Edit customer-scoped fields (branding, regulatory profile, legal name) backing every
 * project for this customer. PR 4 introduces the underlying CustomerDocument; this page is
 * the UI for editing it after import or hand-creation.
 */
export function CustomerSettingsPage({ customerId, onBack }: CustomerSettingsPageProps) {
  const intl = useIntl();
  const getToken = useApiToken();
  const [customer, setCustomer] = useState<CustomerDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState<{ intent: 'success' | 'error' | 'info' | 'warning'; text: string } | null>(null);
  // Create affordance (epic #400) — shown when no CustomerDocument exists yet for the slug.
  // Name fields only; everything else stays PATCH-editable on the form that follows.
  const [createForm, setCreateForm] = useState({ displayName: '', legalName: '' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setStatus(null);
    try {
      const token = await getToken();
      if (!token) return;
      const doc = await getCustomer(customerId, token);
      setCustomer(doc);
      if (!doc) {
        setStatus({
          intent: 'info',
          text: intl.formatMessage({ id: 'customerSettings.noRecord.info' }),
        });
      }
    } catch (err) {
      setStatus({ intent: 'error', text: `Failed to load customer: ${(err as Error).message}` });
    } finally {
      setLoading(false);
    }
  }, [customerId, getToken]);

  useEffect(() => { load(); }, [load]);

  const updateField = <K extends keyof CustomerDocument>(key: K, value: CustomerDocument[K]) => {
    setCustomer(prev => prev ? { ...prev, [key]: value } : prev);
  };

  const updateBranding = (patch: Partial<CustomerDocument['branding']>) => {
    setCustomer(prev => prev ? { ...prev, branding: { ...prev.branding, ...patch } } : prev);
  };

  const onLogoFileChange = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result is "data:image/png;base64,..." — strip the prefix for storage.
      const comma = result.indexOf(',');
      updateBranding({ logoBase64: comma >= 0 ? result.slice(comma + 1) : result });
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!customer) return;
    setSaving(true);
    setStatus(null);
    try {
      const token = await getToken();
      if (!token) return;
      const patch: CustomerPatch = {
        legalName: customer.legalName,
        displayName: customer.displayName,
        country: customer.country,
        sector: customer.sector,
        isPublicSector: customer.isPublicSector,
        regulatoryProfile: customer.regulatoryProfile,
        address: customer.address,
        branding: customer.branding,
        // #503: '' is meaningful here — it clears the alias back to "same as the slug".
        opsAlias: customer.opsAlias ?? '',
      };
      const saved = await patchCustomer(customerId, patch, token);
      setCustomer(saved);
      setStatus({ intent: 'success', text: intl.formatMessage({ id: 'customerSettings.save.success' }) });
    } catch (err) {
      setStatus({ intent: 'error', text: `Failed to save: ${(err as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  const create = async () => {
    setCreating(true);
    setStatus(null);
    try {
      const token = await getToken();
      if (!token) return;
      const doc = await createCustomer(customerId, {
        displayName: createForm.displayName.trim(),
        legalName: createForm.legalName.trim(),
      }, token);
      setCustomer(doc);
      // A slug the Ops Agent does not recognise will never receive a nightly
      // programme snapshot — the weekly report's posture band stays empty and
      // nothing anywhere says why. Surface it at the one moment it is cheap to
      // fix: creation.
      setStatus(doc.opsAliasStatus === 'unknown'
        ? { intent: 'warning', text: intl.formatMessage({ id: 'customerSettings.create.opsAliasUnknown' }, { customerId }) }
        : { intent: 'success', text: intl.formatMessage({ id: 'customerSettings.create.success' }) });
    } catch (err) {
      setStatus({ intent: 'error', text: `Failed to create: ${(err as Error).message}` });
    } finally {
      setCreating(false);
    }
  };

  const runImport = async () => {
    setImporting(true);
    setStatus(null);
    try {
      const token = await getToken();
      if (!token) return;
      const result = await importCustomer(customerId, token);
      setCustomer(result.customer);
      setStatus({
        intent: 'success',
        text: intl.formatMessage(
          { id: 'customerSettings.import.success' },
          {
            logoStatus: result.logoEmbedded
              ? intl.formatMessage({ id: 'customerSettings.import.logoEmbedded' })
              : intl.formatMessage({ id: 'customerSettings.import.logoAbsent' }),
            overrideCount: result.overrideKeysSet.length,
            projectId: result.patchedProjectId ?? intl.formatMessage({ id: 'customerSettings.import.noProject' }),
          },
        ),
      });
    } catch (err) {
      setStatus({ intent: 'error', text: `Import failed: ${(err as Error).message}` });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="page-root page-root--scrollable">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'customerSettings.backButton.title' })} />
        <BuildingRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'customerSettings.title' }, { name: customer?.displayName ?? customerId })}</Text>
        <div className="title-bar-actions">
          <Button
            icon={<ArrowDownloadRegular />}
            onClick={runImport}
            disabled={importing}
            appearance="subtle"
            title={intl.formatMessage({ id: 'customerSettings.importButton.title' })}
          >
            {importing ? intl.formatMessage({ id: 'customerSettings.importButton.importing' }) : intl.formatMessage({ id: 'customerSettings.importButton.label' })}
          </Button>
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            onClick={save}
            disabled={!customer || saving}
          >
            {saving ? intl.formatMessage({ id: 'customerSettings.saveButton.saving' }) : intl.formatMessage({ id: 'customerSettings.saveButton.label' })}
          </Button>
        </div>
      </div>

      {status && (
        <MessageBar intent={status.intent} style={{ marginBottom: '12px' }}>
          <MessageBarBody>{status.text}</MessageBarBody>
        </MessageBar>
      )}

      {loading ? (
        <Spinner label={intl.formatMessage({ id: 'customerSettings.loading' })} size="small" />
      ) : !customer ? (
        <div className="flex-col">
          <Text size={400} weight="semibold">{intl.formatMessage({ id: 'customerSettings.create.heading' })}</Text>
          <Text size={200} className="text-muted">{intl.formatMessage({ id: 'customerSettings.create.description' })}</Text>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.displayName' })} required>
            <Input
              value={createForm.displayName}
              onChange={(_, d) => setCreateForm(f => ({ ...f, displayName: d.value }))}
            />
          </Field>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.legalName' })} required>
            <Input
              value={createForm.legalName}
              onChange={(_, d) => setCreateForm(f => ({ ...f, legalName: d.value }))}
            />
          </Field>
          <div className="flex-row">
            <Button
              appearance="primary"
              icon={<AddRegular />}
              disabled={creating || !createForm.displayName.trim() || !createForm.legalName.trim()}
              onClick={create}
            >
              {creating
                ? intl.formatMessage({ id: 'customerSettings.create.creating' })
                : intl.formatMessage({ id: 'customerSettings.create.btn' })}
            </Button>
          </div>
        </div>
      ) : (
        <div className="customer-settings-form" style={{ display: 'grid', gap: '16px', maxWidth: '720px' }}>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.displayName' })}>
            <Input
              value={customer?.displayName ?? ''}
              onChange={(_, d) => updateField('displayName', d.value)}
            />
          </Field>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.legalName' })}>
            <Input
              value={customer?.legalName ?? ''}
              onChange={(_, d) => updateField('legalName', d.value)}
            />
          </Field>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.sector' })}>
            <Input
              value={customer?.sector ?? ''}
              placeholder={intl.formatMessage({ id: 'customerSettings.field.sector.placeholder' })}
              onChange={(_, d) => updateField('sector', d.value)}
            />
          </Field>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.country' })}>
            <Input
              value={customer?.country ?? ''}
              onChange={(_, d) => updateField('country', d.value)}
            />
          </Field>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.regulatoryProfile' })}>
            <Input
              value={customer?.regulatoryProfile ?? ''}
              placeholder={intl.formatMessage({ id: 'customerSettings.field.regulatoryProfile.placeholder' })}
              onChange={(_, d) => updateField('regulatoryProfile', d.value)}
            />
          </Field>
          <Field
            label={intl.formatMessage({ id: 'customerSettings.field.opsAlias' })}
            hint={intl.formatMessage({ id: 'customerSettings.field.opsAlias.hint' }, { customerId })}
          >
            <Input
              value={customer?.opsAlias ?? ''}
              placeholder={customerId}
              onChange={(_, d) => updateField('opsAlias', d.value)}
            />
          </Field>
          <Field>
            <Switch
              checked={customer?.isPublicSector ?? false}
              label={intl.formatMessage({ id: 'customerSettings.field.publicSector' })}
              onChange={(_, d) => updateField('isPublicSector', d.checked)}
            />
          </Field>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.address' })}>
            <Textarea
              value={customer?.address ?? ''}
              onChange={(_, d) => updateField('address', d.value)}
              rows={2}
            />
          </Field>

          <Text size={400} weight="semibold" style={{ marginTop: '8px' }}>
            <ImageRegular /> {intl.formatMessage({ id: 'customerSettings.section.branding' })}
          </Text>
          <Text size={200} className="text-muted">
            {intl.formatMessage({ id: 'customerSettings.branding.description' })}
          </Text>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.logo' })}>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {customer?.branding?.logoBase64 ? (
                <img
                  src={`data:image/png;base64,${customer.branding.logoBase64}`}
                  alt={intl.formatMessage({ id: 'customerSettings.logo.alt' })}
                  style={{ maxHeight: '64px', maxWidth: '200px', objectFit: 'contain', border: '1px solid var(--colorNeutralStroke2)' }}
                />
              ) : (
                <Text size={200} className="text-muted">{intl.formatMessage({ id: 'customerSettings.logo.none' })}</Text>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={e => onLogoFileChange(e.target.files?.[0] ?? null)}
              />
            </div>
          </Field>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.primaryColour' })}>
            <Input
              value={customer?.branding?.primaryColour ?? ''}
              placeholder="#1B5E20"
              onChange={(_, d) => updateBranding({ primaryColour: d.value })}
            />
          </Field>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.secondaryColour' })}>
            <Input
              value={customer?.branding?.secondaryColour ?? ''}
              placeholder="#0D47A1"
              onChange={(_, d) => updateBranding({ secondaryColour: d.value })}
            />
          </Field>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.accentColour' })}>
            <Input
              value={customer?.branding?.accentColour ?? ''}
              placeholder="#FBC02D"
              onChange={(_, d) => updateBranding({ accentColour: d.value })}
            />
          </Field>
          <Field label={intl.formatMessage({ id: 'customerSettings.field.textColour' })}>
            <Input
              value={customer?.branding?.textColour ?? ''}
              placeholder="#212121"
              onChange={(_, d) => updateBranding({ textColour: d.value })}
            />
          </Field>
        </div>
      )}
    </div>
  );
}
